import axios, { AxiosHeaders, InternalAxiosRequestConfig } from 'axios'
import { AuthenticationClient } from '../../features/authentication/slice/authenticationSlice'
import { isEhrAuthSession } from '../../features/ehr-launch/ehrLaunchStorage'
import { emitEhrSessionInvalidated } from '../../features/ehr-launch/ehrSessionEvents'

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  withCredentials: true,
})

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('access_token')
    ? `Bearer ${localStorage.getItem('access_token')}`
    : ''

  const prescriberStr = localStorage.getItem('prescriber')
  const prescriberId =
    prescriberStr !== 'undefined' && prescriberStr?.length ? JSON.parse(prescriberStr).id : ''

  const headers = AxiosHeaders.from(config.headers)
  headers.set('Authorization', token)
  headers.set('Prescriber-Id', prescriberId)
  headers.set('Id-Token', localStorage.getItem('id_token') || '')
  config.headers = headers

  return config
})

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

/**
 * The only two @Public() endpoints on the BFF's auth controller. Refreshing in
 * response to the refresh call itself would await the very promise that is
 * still settling, and hang forever - so these two are skipped.
 *
 * Matched exactly, never by prefix. Every other /auth/* route is authenticated
 * (/auth/login, /auth/verify, /auth/token/revoke, /auth/send-email-code,
 * /auth/verify-email-code and the whole auth/ehr-launch controller) and must
 * keep refresh-and-retry on 401. A /^\/auth(\/|$)/ style prefix match silently
 * disabled refresh for all eight of them, which reproduces the very
 * "I had to sign in again" symptom this file is meant to fix.
 */
const PUBLIC_TOKEN_ENDPOINTS = new Set(['/auth', '/auth/refresh'])

const isTokenEndpoint = (url?: string): boolean =>
  !!url && PUBLIC_TOKEN_ENDPOINTS.has(url.split('?')[0])

/**
 * A single in-flight refresh shared by every 401. Without this, concurrent
 * requests each fired their own /auth/refresh - RTK Query alone mounts several
 * queries per page - and all but the first tended to fail. A failure on the
 * login call surfaced to the user as being signed out, even though the refresh
 * token was still perfectly valid.
 */
let refreshPromise: Promise<string | null> | null = null

const refreshAccessTokenOnce = (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise

  const clientId = localStorage.getItem('client_id')
  const refreshToken = localStorage.getItem('refresh_token')
  if (!clientId || !refreshToken) return Promise.resolve(null)

  refreshPromise = AuthenticationClient.refreshAccessToken({ clientId, refreshToken })
    .then(({ data }) => {
      if (!data?.assessToken) return null
      localStorage.setItem('access_token', data.assessToken)
      // The refresh returns a fresh id_token as well. Not storing it left a
      // stale one in localStorage, which the BFF decodes to build the
      // login-email / first-name / last-name request headers.
      if (data.idToken) localStorage.setItem('id_token', data.idToken)
      return data.assessToken
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config as RetriableConfig | undefined

    if (error?.response?.status === 401 && isEhrAuthSession()) {
      emitEhrSessionInvalidated()
      return Promise.reject(error)
    }

    // Only 401 means "this access token is stale". A 5xx is an outage on the
    // BFF side and must not be mistaken for an expired session.
    if (
      error?.response?.status !== 401 ||
      !config ||
      config._retried ||
      isTokenEndpoint(config.url)
    ) {
      return Promise.reject(error)
    }
    config._retried = true

    const accessToken = await refreshAccessTokenOnce()
    if (!accessToken) {
      return Promise.reject(error)
    }

    // Retry through `instance` rather than bare axios, so the request
    // interceptor re-applies the freshly stored token and the other headers.
    return instance(config)
  }
)

export default instance
